import { Routes } from '@angular/router';
import { authGuard } from './core/guards/auth.guard';
import {
  permissionGuard,
  permissionsGuard,
} from './core/guards/permission.guard';
import { featureGuard } from './core/guards/feature.guard';
import { PERMISSIONS } from './shared/constants/permissions';
import { FEATURES } from './shared/constants/features';
import { AppLayout } from './layout/components/app.layout';
import { AuthLayout } from './layout/components/app.authlayout';
import { LandingLayout } from './layout/components/app.landinglayout';

export const routes: Routes = [
  {
    path: '',
    component: AppLayout,
    canActivate: [authGuard],
    children: [
      {
        path: 'dashboard',
        data: { title: 'Dashboard', breadcrumb: 'Dashboard' },
        loadComponent: () =>
          import('./features/dashboard/views/dashboard/dashboard.component').then(
            (c) => c.DashboardComponent,
          ),
      },
      {
        path: 'admin',
        data: { title: 'Administración', breadcrumb: 'Administración' },
        loadChildren: () =>
          import('./features/admin/admin.routes').then((c) => c.ADMIN_ROUTES),
        canActivate: [
          permissionsGuard([PERMISSIONS.Users.Read, PERMISSIONS.Roles.Read]),
        ],
      },
      {
        path: 'order',
        data: { title: 'Órdenes', breadcrumb: 'Órdenes' },
        loadChildren: () =>
          import('./features/order/order.routes').then((c) => c.ORDER_ROUTES),
        canActivate: [permissionsGuard([PERMISSIONS.Orders.Read])],
      },
      {
        path: 'template',
        data: { title: 'Plantillas', breadcrumb: 'Plantillas' },
        loadChildren: () =>
          import('./features/template/template.routes').then(
            (c) => c.TEMPLATE_ROUTES,
          ),
        canActivate: [permissionGuard(PERMISSIONS.Templates.Read)],
      },
      {
        path: 'field',
        data: { title: 'Campos de Orden', breadcrumb: 'Campos de Orden' },
        loadChildren: () =>
          import('./features/field/field.routes').then((c) => c.FIELD_ROUTES),
        canActivate: [permissionGuard(PERMISSIONS.Fields.Read)],
      },
      {
        path: 'customer',
        data: { title: 'Clientes', breadcrumb: 'Clientes' },
        loadChildren: () =>
          import('./features/customer/customer.routes').then(
            (c) => c.CUSTOMER_ROUTES,
          ),
        canActivate: [
          permissionsGuard([PERMISSIONS.Customers.Read]),
          featureGuard(FEATURES.Customers),
        ],
      },
      {
        path: 'production-line',
        data: {
          title: 'Líneas de Producción',
          breadcrumb: 'Líneas de Producción',
        },
        loadChildren: () =>
          import('./features/production-line/production-line.routes').then(
            (c) => c.PRODUCTION_LINE_ROUTES,
          ),
        canActivate: [
          permissionGuard(PERMISSIONS.ProductionLines.Read),
          featureGuard(FEATURES.ProductionLines),
        ],
      },
      {
        path: 'inventory',
        data: { title: 'Inventario', breadcrumb: 'Inventario' },
        loadChildren: () =>
          import('./features/inventory/inventory.routes').then(
            (c) => c.INVENTORY_ROUTES,
          ),
        canActivate: [
          permissionGuard(PERMISSIONS.Inventory.Read),
          featureGuard(FEATURES.Inventory),
        ],
      },
      {
        path: 'settings',
        data: { title: 'Configuración', breadcrumb: 'Configuración' },
        loadChildren: () =>
          import('./features/settings/settings.routes').then(
            (c) => c.SETTINGS_ROUTES,
          ),
        canActivate: [
          permissionsGuard([
            PERMISSIONS.PermissionsCatalog.Read,
            PERMISSIONS.Integrations.ManageSiigoCredentials,
          ]),
        ],
      },
      {
        path: 'profile',
        data: { title: 'Mi Perfil', breadcrumb: 'Mi Perfil' },
        loadChildren: () =>
          import('./features/user/user.routes').then((c) => c.USERS_ROUTES),
      },
      {
        path: 'billing',
        data: { title: 'Suscripción', breadcrumb: 'Suscripción' },
        loadComponent: () =>
          import('./features/billing/views/billing-view/billing-view.component').then(
            (c) => c.BillingViewComponent,
          ),
      },
      {
        path: '',
        pathMatch: 'full',
        redirectTo: 'dashboard',
      },
    ],
  },
  {
    path: 'landing',
    component: LandingLayout,
    children: [
      {
        path: '',
        data: { title: 'PlanTrack' },
        loadComponent: () =>
          import('./pages/landing/landing.page').then((c) => c.LandingPage),
      },
      {
        path: 'privacy-policy',
        data: { title: 'Política de Privacidad' },
        loadComponent: () =>
          import('./pages/legal/privacy-policy/privacy-policy.page').then(
            (c) => c.PrivacyPolicyPage,
          ),
      },
      {
        path: 'terms-of-service',
        data: { title: 'Términos y Condiciones' },
        loadComponent: () =>
          import('./pages/legal/terms-of-service/terms-of-service.page').then(
            (c) => c.TermsOfServicePage,
          ),
      },
    ],
  },
  {
    path: 'auth',
    component: AuthLayout,
    children: [
      {
        path: 'login',
        data: { title: 'Iniciar Sesión' },
        loadComponent: () =>
          import('./pages/auth/login/login.page').then((c) => c.LoginPage),
      },
      {
        path: 'access-denied',
        data: { title: 'Acceso Denegado' },
        loadComponent: () =>
          import('./pages/auth/access-denied/access-denied.page').then(
            (c) => c.AccessDeniedPage,
          ),
      },
      {
        path: 'forgot-password',
        data: { title: 'Recuperar Contraseña' },
        loadComponent: () =>
          import('./pages/auth/forgot-password/forgot-password.page').then(
            (c) => c.ForgotPasswordPage,
          ),
      },
      {
        path: 'reset-password',
        data: { title: 'Restablecer Contraseña' },
        loadComponent: () =>
          import('./pages/auth/reset-password/reset-password.page').then(
            (c) => c.ResetPasswordPage,
          ),
      },
      {
        path: 'confirm-email',
        data: { title: 'Confirmar Correo Electrónico' },
        loadComponent: () =>
          import('./pages/auth/confirm-email/confirm-email.page').then(
            (c) => c.ConfirmEmailPage,
          ),
      },
      {
        path: '',
        redirectTo: 'login',
        pathMatch: 'full',
      },
    ],
  },
  {
    path: '**',
    redirectTo: 'landing',
  },
];
